import React from 'react';

// Reports chart primitives — ported from the Rush Studio design's
// reports-charts.jsx. SVG charts scale to container width (width:100%;
// height:auto) preserving aspect ratio. Colors come from the shared rush.css
// design tokens (var(--accent), --green, --indigo, --line, --bg-card, …).

export const ICN = {
    up: (<svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M6 9.5V3M6 3L3 6M6 3l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    down: (<svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M6 2.5V9M6 9L3 6M6 9l3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    flat: (<svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2.5 6h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>),
    arrow: (<svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    star: (<svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1.6l1.8 3.9 4.2.5-3.1 2.9.8 4.2L8 11.9 4.3 13l.8-4.2L2 5.9l4.2-.5L8 1.6z" /></svg>),
};

export const niceMax = (m, step = 50) => Math.max(step, Math.ceil((m || 0) / step) * step);

// ── KPI card ─────────────────────────────────────────────────────────────────
export const Kpi = ({ label, value, unit, delta, arrow = 'up', tone = 'good', spark, color = 'var(--accent)' }) => (
    <div className="rpt-kpi">
        <div className="lbl">{label}{unit && <span className="u">{unit}</span>}</div>
        <div className="val">{value}</div>
        <div className={`delta ${tone}`}>
            {arrow === 'up' ? ICN.up : arrow === 'down' ? ICN.down : ICN.flat}{delta}
        </div>
        {spark && spark.length > 0 && <Spark data={spark} color={color} />}
    </div>
);

// ── Sparkline ─────────────────────────────────────────────────────────────────
export const Spark = ({ data, color = 'var(--accent)', w = 120, h = 34 }) => {
    if (!data || data.length < 2) return <svg className="rpt-spark" viewBox={`0 0 ${w} ${h}`} />;
    const pad = 3;
    const max = Math.max(...data); const min = Math.min(...data);
    const range = max - min || 1;
    const step = (w - pad * 2) / (data.length - 1);
    const pts = data.map((v, i) => [pad + i * step, h - pad - ((v - min) / range) * (h - pad * 2)]);
    const d = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
    const area = d + ` L ${pts[pts.length - 1][0]} ${h} L ${pts[0][0]} ${h} Z`;
    return (
        <svg className="rpt-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" fill="none">
            <path d={area} fill={color} opacity="0.12" />
            <path d={d} stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

// ── Section card ──────────────────────────────────────────────────────────────
export const Card = ({ title, sub, legend, right, children, className = '', span }) => (
    <div className={`rs-card rpt-card ${className}`} style={span ? { gridColumn: `span ${span}` } : undefined}>
        {(title || legend || right || sub) && (
            <div className="rpt-h">
                {title && <h3>{title}</h3>}
                {legend && <Legend items={legend} />}
                <div className="grow" />
                {sub && <span className="rpt-sub">{sub}</span>}
                {right}
            </div>
        )}
        {children}
    </div>
);

export const Legend = ({ items }) => (
    <div className="rpt-legend">
        {items.map((it) => (
            <span key={it.label}><i className="sw" style={{ background: it.color }} />{it.label}</span>
        ))}
    </div>
);

export const Trend = ({ dir }) => (
    <span className={`rpt-trend ${dir}`}>{dir === 'up' ? ICN.up : dir === 'down' ? ICN.down : ICN.flat}</span>
);

// ── Stacked area (throughput) ──────────────────────────────────────────────────
export const Area = ({ labels, series, w = 720, h = 240, unit = 'k' }) => {
    const padL = 38; const padR = 12; const padT = 14; const padB = 28;
    const n = labels.length;
    const totals = labels.map((_x, i) => series.reduce((s, ser) => s + (ser.data[i] || 0), 0));
    const mx = niceMax(Math.max(0, ...totals), 50);
    const xOf = (i) => padL + (w - padL - padR) * (i / (n - 1 || 1));
    const yOf = (v) => padT + (h - padT - padB) * (1 - v / mx);
    const lowers = series.map(() => new Array(n).fill(0));
    for (let i = 0; i < n; i += 1) { let acc = 0; for (let s = 0; s < series.length; s += 1) { lowers[s][i] = acc; acc += series[s].data[i] || 0; } }
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(mx * f));
    return (
        <svg className="rpt-svg" viewBox={`0 0 ${w} ${h}`} fill="none">
            {ticks.map((tk, i) => { const y = yOf(tk); return (
                <g key={i}>
                    <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="var(--line)" strokeWidth="1" />
                    <text x={padL - 8} y={y + 3.5} textAnchor="end" className="rpt-axis">{tk}{unit}</text>
                </g>); })}
            {series.map((ser, s) => {
                const up = ser.data.map((v, i) => [xOf(i), yOf(lowers[s][i] + (v || 0))]);
                const lo = ser.data.map((v, i) => [xOf(i), yOf(lowers[s][i])]);
                const d = up.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ')
                    + ' ' + lo.slice().reverse().map((p) => 'L' + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ') + ' Z';
                const top = up.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
                return (<g key={s}><path d={d} fill={ser.color} opacity={ser.fill != null ? ser.fill : 0.85} /><path d={top} stroke={ser.color} strokeWidth="1" opacity="0.4" /></g>);
            })}
            {labels.map((l, i) => (l ? <text key={i} x={xOf(i)} y={h - padB + 16} textAnchor="middle" className="rpt-axis">{l}</text> : null))}
        </svg>
    );
};

// ── Bars (grouped or stacked) ──────────────────────────────────────────────────
export const Bars = ({ labels, series, w = 720, h = 240, unit = 'k', stacked = true, step = 50 }) => {
    const padL = 38; const padR = 12; const padT = 14; const padB = 28;
    const n = labels.length; const m = series.length;
    const totals = labels.map((_x, i) => (stacked ? series.reduce((s, ser) => s + (ser.data[i] || 0), 0) : Math.max(...series.map((ser) => ser.data[i] || 0))));
    const mx = niceMax(Math.max(0, ...totals), step);
    const slot = (w - padL - padR) / n;
    const yOf = (v) => padT + (h - padT - padB) * (1 - v / mx);
    const bw = stacked ? Math.min(46, slot * 0.46) : Math.min(20, (slot * 0.6) / m);
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(mx * f));
    return (
        <svg className="rpt-svg" viewBox={`0 0 ${w} ${h}`} fill="none">
            {ticks.map((tk, i) => { const y = yOf(tk); return (
                <g key={i}><line x1={padL} y1={y} x2={w - padR} y2={y} stroke="var(--line)" strokeWidth="1" /><text x={padL - 8} y={y + 3.5} textAnchor="end" className="rpt-axis">{tk}{unit}</text></g>); })}
            {labels.map((l, i) => {
                const cx = padL + slot * i + slot / 2;
                if (stacked) {
                    let acc = 0;
                    return (<g key={i}>
                        {series.map((ser, s) => { const v = ser.data[i] || 0; const y = yOf(acc + v); const hgt = (h - padT - padB) * (v / mx); acc += v;
                            return <rect key={s} x={cx - bw / 2} y={y} width={bw} height={Math.max(0, hgt)} fill={ser.color} rx="3"><title>{ser.name}: {v}{unit}</title></rect>; })}
                        <text x={cx} y={h - padB + 16} textAnchor="middle" className="rpt-axis">{l}</text>
                    </g>);
                }
                const groupW = bw * m + (m - 1) * 3;
                return (<g key={i}>
                    {series.map((ser, s) => { const v = ser.data[i] || 0; const x = cx - groupW / 2 + s * (bw + 3); const y = yOf(v); const hgt = (h - padT - padB) * (v / mx);
                        return <rect key={s} x={x} y={y} width={bw} height={Math.max(0, hgt)} fill={ser.color} rx="2.5"><title>{ser.name}: {v}{unit}</title></rect>; })}
                    <text x={cx} y={h - padB + 16} textAnchor="middle" className="rpt-axis">{l}</text>
                </g>);
            })}
        </svg>
    );
};

// ── Line (with optional target line) ────────────────────────────────────────────
export const Line = ({ data, w = 360, h = 180, min, max, ticks, target, targetLabel, color = 'var(--accent)', unit = '' }) => {
    if (!data || !data.length) return <svg className="rpt-svg" viewBox={`0 0 ${w} ${h}`} />;
    const padL = 32; const padR = 10; const padT = 14; const padB = 24;
    const lo = min != null ? min : Math.min(...data);
    const hi = max != null ? max : Math.max(...data);
    const tk = ticks || [lo, (lo + hi) / 2, hi];
    const step = (w - padL - padR) / (data.length - 1 || 1);
    const yOf = (v) => padT + (h - padT - padB) * (1 - (v - lo) / ((hi - lo) || 1));
    const pts = data.map((v, i) => [padL + i * step, yOf(v)]);
    const d = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
    const area = d + ` L ${pts[pts.length - 1][0]} ${h - padB} L ${pts[0][0]} ${h - padB} Z`;
    return (
        <svg className="rpt-svg" viewBox={`0 0 ${w} ${h}`} fill="none">
            {tk.map((t, i) => { const y = yOf(t); return <g key={i}><line x1={padL} y1={y} x2={w - padR} y2={y} stroke="var(--line)" strokeWidth="1" /><text x={padL - 7} y={y + 3.5} textAnchor="end" className="rpt-axis">{t}{unit}</text></g>; })}
            {target != null && (<g><line x1={padL} y1={yOf(target)} x2={w - padR} y2={yOf(target)} stroke="var(--ink-4)" strokeWidth="1.2" strokeDasharray="4 4" /><text x={w - padR} y={yOf(target) - 5} textAnchor="end" className="rpt-axis" style={{ fontWeight: 600 }}>{targetLabel || (`target ${target}${unit}`)}</text></g>)}
            <path d={area} fill={color} opacity="0.1" />
            <path d={d} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="2.6" fill="var(--bg-card)" stroke={color} strokeWidth="1.6" />)}
        </svg>
    );
};

// ── Donut ────────────────────────────────────────────────────────────────────
export const Donut = ({ segments, size = 150, thickness = 18, center, sub }) => {
    const r = (size - thickness) / 2;
    const C = 2 * Math.PI * r;
    let offset = 0;
    return (
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="rpt-donut">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-soft)" strokeWidth={thickness} />
            {segments.map((s, i) => { const len = (s.value / 100) * C;
                const el = <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={thickness} strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform={`rotate(-90 ${size / 2} ${size / 2})`} strokeLinecap="butt"><title>{s.label}: {s.value}%</title></circle>;
                offset += len; return el; })}
            {center != null && <text x={size / 2} y={size / 2 - (sub ? 5 : 1)} textAnchor="middle" dominantBaseline="middle" className="rpt-donut-c">{center}</text>}
            {sub && <text x={size / 2} y={size / 2 + 14} textAnchor="middle" dominantBaseline="middle" className="rpt-donut-s">{sub}</text>}
        </svg>
    );
};

// ── Heatmap ────────────────────────────────────────────────────────────────────
export const Heat = ({ cols, rows, hue = 'accent', fmt = (v) => v, max, cell = 38, labelW = 116 }) => {
    const allVals = rows.flatMap((r) => r[1]);
    const mx = max != null ? max : Math.max(0, ...allVals);
    const colorVar = `var(--${hue})`;
    return (
        <div className="rpt-heat" style={{ gridTemplateColumns: `${labelW}px repeat(${cols.length}, minmax(0, 1fr))` }}>
            <div className="rpt-heat-corner" />
            {cols.map((c) => <div key={c} className="rpt-heat-col">{c}</div>)}
            {rows.map(([label, vals]) => (
                <React.Fragment key={label}>
                    <div className="rpt-heat-row">{label}</div>
                    {vals.map((v, i) => {
                        const intensity = v === 0 || !mx ? 0 : 14 + Math.round((v / mx) * 78);
                        return (
                            <div key={i} className={`rpt-heat-cell ${intensity > 55 ? 'hot' : ''}`} style={{ height: cell, background: v === 0 ? 'var(--bg-soft)' : `color-mix(in oklab, ${colorVar} ${intensity}%, var(--bg-card))` }} title={`${label} · ${cols[i]}: ${fmt(v)}`}>
                                {v !== 0 && <span>{fmt(v)}</span>}
                            </div>
                        );
                    })}
                </React.Fragment>
            ))}
        </div>
    );
};

// ── Scatter ────────────────────────────────────────────────────────────────────
export const Scatter = ({ points, w = 460, h = 300, xMin, xMax, yMin, yMax, xLabel, yLabel, xUnit = '', yUnit = '', xTicks, yTicks }) => {
    const padL = 42; const padR = 14; const padT = 14; const padB = 36;
    const xlo = xMin; const xhi = xMax; const ylo = yMin; const yhi = yMax;
    const xOf = (v) => padL + (w - padL - padR) * ((v - xlo) / ((xhi - xlo) || 1));
    const yOf = (v) => padT + (h - padT - padB) * (1 - (v - ylo) / ((yhi - ylo) || 1));
    const xt = xTicks || [xlo, (xlo + xhi) / 2, xhi];
    const yt = yTicks || [ylo, (ylo + yhi) / 2, yhi];
    return (
        <svg className="rpt-svg" viewBox={`0 0 ${w} ${h}`} fill="none">
            {yt.map((t, i) => { const y = yOf(t); return <g key={`y${i}`}><line x1={padL} y1={y} x2={w - padR} y2={y} stroke="var(--line)" strokeWidth="1" /><text x={padL - 8} y={y + 3.5} textAnchor="end" className="rpt-axis">{t}{yUnit}</text></g>; })}
            {xt.map((t, i) => { const x = xOf(t); return <text key={`x${i}`} x={x} y={h - padB + 16} textAnchor="middle" className="rpt-axis">{Math.round(t)}{xUnit}</text>; })}
            {xLabel && <text x={(padL + w - padR) / 2} y={h - 4} textAnchor="middle" className="rpt-axis-t">{xLabel}</text>}
            {yLabel && <text transform={`rotate(-90 12 ${(padT + h - padB) / 2})`} x={12} y={(padT + h - padB) / 2} textAnchor="middle" className="rpt-axis-t">{yLabel}</text>}
            {points.map((p, i) => (
                <g key={i}>
                    <circle cx={xOf(p.x)} cy={yOf(p.y)} r={p.r || 7} fill={p.color || 'var(--accent)'} opacity="0.82" stroke="var(--bg-card)" strokeWidth="1.5"><title>{p.label}: {Math.round(p.x)}{xUnit} · {p.y}{yUnit}</title></circle>
                    {p.tag && <text x={xOf(p.x)} y={yOf(p.y) - (p.r || 7) - 4} textAnchor="middle" className="rpt-scatter-tag">{p.tag}</text>}
                </g>
            ))}
        </svg>
    );
};

// ── Horizontal stacked bars (MQM severity) ──────────────────────────────────────
export const HBars = ({ rows, segs, max, unit = '', fmt = (v) => v }) => {
    const totals = rows.map((r) => r.slice(1).reduce((s, v) => s + v, 0));
    const mx = max != null ? max : Math.max(1, ...totals);
    return (
        <div className="rpt-hbars">
            {rows.map((r, ri) => {
                const total = totals[ri];
                return (
                    <div className="rpt-hbar-row" key={r[0]}>
                        <span className="lbl">{r[0]}</span>
                        <span className="track">
                            {segs.map((sg, si) => { const v = r[si + 1]; if (!v) return null; const wd = (v / mx) * 100;
                                return <span key={si} className="seg" style={{ width: `${wd}%`, background: sg.color }} title={`${sg.name}: ${v}`} />; })}
                        </span>
                        <span className="tot">{fmt(total)}{unit}</span>
                    </div>
                );
            })}
        </div>
    );
};

// ── Inline progress bar ──────────────────────────────────────────────────────
export const Meter = ({ value, max = 100, color = 'var(--accent)', height = 7 }) => (
    <span className="rpt-meter" style={{ height }}>
        <span style={{ width: `${Math.min(100, (value / (max || 1)) * 100)}%`, background: color }} />
    </span>
);
